/**
 * GlobeIQ Console Bridge  –  globeiq-bridge.js
 *
 * This script runs before Angular bootstraps. It exposes window.globeiq
 * with a magic "mode" setter so developers can toggle dark mode straight
 * from the browser DevTools console without reloading the page.
 *
 * Usage:
 *   globeiq.mode = 'dark'    // enable dark mode
 *   globeiq.mode = 'light'   // restore light mode
 *   globeiq.mode             // read current mode
 *   globeiq.toggle()         // flip between modes
 *
 * The choice is persisted in localStorage under "giq-theme" and is
 * automatically restored by ThemeService on every page load.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'giq-theme';
  var DARK_ATTR   = 'data-theme';
  var DARK_VALUE  = 'dark';

  // ── Helpers ──────────────────────────────────────────────────────────────

  function getMode() {
    return document.documentElement.getAttribute(DARK_ATTR) === DARK_VALUE
      ? 'dark'
      : 'light';
  }

  function applyMode(mode) {
    var normalised = (typeof mode === 'string') ? mode.trim().toLowerCase() : '';

    if (normalised !== 'dark' && normalised !== 'light') {
      console.warn(
        '[GlobeIQ] Unknown mode "' + mode + '". Use "dark" or "light".'
      );
      return;
    }

    if (normalised === 'dark') {
      document.documentElement.setAttribute(DARK_ATTR, DARK_VALUE);
    } else {
      document.documentElement.removeAttribute(DARK_ATTR);
    }

    try {
      localStorage.setItem(STORAGE_KEY, normalised);
    } catch (e) { /* storage unavailable – ignore */ }

    console.log('[GlobeIQ] Theme set to: ' + normalised);

    // If ThemeService is already up, keep it in sync.
    if (window.__giqThemeService && typeof window.__giqThemeService.setMode === 'function') {
      window.__giqThemeService.setMode(normalised);
    }
  }

  function toggle() {
    applyMode(getMode() === 'dark' ? 'light' : 'dark');
  }

  // ── Restore persisted preference ─────────────────────────────────────────

  try {
    var saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') {
      applyMode(saved);
    }
  } catch (e) { /* ignore */ }

  // ── Expose window.globeiq ─────────────────────────────────────────────────

  var _mode = getMode();

  var globeiq = window.globeiq || {};

  Object.defineProperty(globeiq, 'mode', {
    get: function () {
      return getMode();
    },
    set: function (value) {
      applyMode(value);
    },
    enumerable: true,
    configurable: true
  });

  globeiq.toggle = toggle;

  globeiq.triggerSessionExpiry = function () {
    // Set expires_at to just over 5 minutes from now.
    // AppComponent listens to the storage event and calls tokenTimeChanged(),
    // which sets the dialog timer to ~1 second. The dialog appears shortly after.
    localStorage.setItem('expires_at', String(Date.now() + 301000));
    // Dispatch a storage event so same-tab listeners (AppComponent) pick it up.
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'expires_at',
      newValue: String(Date.now() + 301000),
      storageArea: localStorage,
    }));
    console.log('[GlobeIQ] Session expiry dialog will appear in ~1 second.');
  };

  globeiq.help = function () {
    console.log(
      '%cGlobeIQ Console API\n' +
      '%c' +
      '  globeiq.mode = "dark"          → Enable dark mode\n' +
      '  globeiq.mode = "light"         → Restore light mode\n' +
      '  globeiq.toggle()               → Flip current mode\n' +
      '  globeiq.mode                   → Read current mode\n' +
      '  globeiq.triggerSessionExpiry() → Show session expiry dialog',
      'font-weight:bold;font-size:14px;color:#0b5c8e',
      'font-size:12px;color:#555'
    );
  };

  window.globeiq = globeiq;

  // Friendly greeting in the console
  console.log(
    '%cGlobeIQ Portal%c  –  type %cglobeiq.help()%c for console commands',
    'font-weight:bold;color:#0b5c8e',
    'color:#555',
    'font-family:monospace;color:#2c974a',
    'color:#555'
  );

})();
